import styles from '../Disco.module.css';
import { useTheme } from '../../../../../../hooks/useTheme';
import Dismiss from '../../../../../ui/Dismiss/Dismiss';
import { FeaturedImage } from './FeaturedImage';
import { AdContent } from './AdContent';
import { ActionButtons } from './ActionButtons';
import { Counter } from './Counter';
import { DotIndicator } from './DotIndicator';
import { SponsoredPill } from './SponsoredPill';
import type { DiscoOffer } from '../types';

interface LayoutProps {
  variant: 'inline' | 'drawer' | 'cover';
  onDismiss?: () => void;
  heading: string;
  subheading: string;
  disclosureDetails?: DiscoOffer['disclosure_details'];
  jsonContent?: unknown;
  showImage: boolean;
  imageUrl?: string;
  imageAlt: string;
  imageTargetUrl?: string;
  offerId?: string;
  acceptCopy: string;
  declineCopy: string;
  onAccept: () => void;
  onDecline: () => void;
  onAssetClick: (targetUrl: string) => void;
  currentIndex: number;
  totalCount: number;
  widgetRef: React.RefCallback<HTMLDivElement>;
  adRef: React.RefCallback<HTMLDivElement>;
  advertiserLogoUrl?: string;
  coverGradientStart?: string;
}

export function Layout({
  variant,
  heading,
  subheading,
  disclosureDetails,
  jsonContent,
  showImage,
  imageUrl,
  imageAlt,
  imageTargetUrl,
  offerId,
  acceptCopy,
  declineCopy,
  onAccept,
  onDecline,
  onAssetClick,
  currentIndex,
  totalCount,
  widgetRef,
  adRef,
  advertiserLogoUrl,
  onDismiss,
  coverGradientStart,
}: LayoutProps) {
  const theme = useTheme();
  const isInline = variant === 'inline';
  const isCover = variant === 'cover';

  if (isCover) {
    const brandColor =
      theme.buttonsPrimaryDefault?.backgroundColor ?? '#2563eb';
    const gradientStart = coverGradientStart ?? `${brandColor}33`;
    const coverImageSrc = showImage && imageUrl ? imageUrl : advertiserLogoUrl;

    return (
      <div
        ref={widgetRef}
        className={styles.coverCarousel}
        style={{ fontFamily: theme.bodytextDefaultNone?.fontFamily }}
      >
        <div ref={adRef} className={styles.coverCarouselLeft}>
          <div className={styles.coverCarouselLeftTop}>
            <SponsoredPill />

            <h2
              className={styles.heading}
              style={{
                fontFamily: theme.headersMediumNone?.fontFamily,
                color: theme.headersMediumNone?.color,
              }}
            >
              {heading}
            </h2>

            <div className={styles.textContent}>
              <AdContent
                subheading={subheading}
                disclosureDetails={disclosureDetails}
                jsonContent={jsonContent}
                bodyStyle={theme.bodytextDefaultNone}
              />
            </div>

            <ActionButtons
              acceptCopy={acceptCopy}
              declineCopy={declineCopy}
              onAccept={onAccept}
              onDecline={onDecline}
            />
          </div>

          <div className={styles.coverDivider} />

          <div className={styles.coverCarouselLeftBottom}>
            <DotIndicator current={currentIndex - 1} total={totalCount} />
          </div>
        </div>

        <div
          className={styles.coverCarouselRight}
          style={{
            background: `linear-gradient(135deg, ${gradientStart}, #ffffff)`,
          }}
        >
          {onDismiss && (
            <div className={styles.mobileCoverCloseBtn}>
              <Dismiss onClick={onDismiss} fill="#666" />
            </div>
          )}
          {coverImageSrc && (
            <img
              key={offerId}
              className={styles.coverLogoImage}
              src={coverImageSrc}
              alt=""
            />
          )}
        </div>
      </div>
    );
  }

  const containerClassName = isInline
    ? `${styles.container} ${styles.inlineLayout}`
    : styles.container;

  const containerStyle = {
    fontFamily: theme.bodytextDefaultNone?.fontFamily,
    ...(isInline ? {} : { padding: '1rem 1.5rem' }),
  };

  const headingClassName = isInline
    ? styles.heading
    : `${styles.heading} ${showImage ? styles.headingWithImage : ''}`;

  const renderImage = () =>
    showImage &&
    imageUrl && (
      <div className={styles.imageContent}>
        <FeaturedImage
          imageUrl={imageUrl}
          alt={imageAlt}
          targetUrl={imageTargetUrl}
          onAssetClick={onAssetClick}
          offerId={offerId}
        />
      </div>
    );

  const sponsoredPill = <SponsoredPill />;
  const headingEl = (
    <h2
      className={headingClassName}
      style={{
        fontFamily: theme.headersMediumNone?.fontFamily,
        color: theme.headersMediumNone?.color,
      }}
    >
      {heading}
    </h2>
  );

  return (
    <div ref={widgetRef} className={containerClassName} style={containerStyle}>
      <div ref={adRef} className={styles.adContent}>
        {!isInline && renderImage()}
        {!isInline && sponsoredPill}
        {!isInline && headingEl}

        <div className={styles.contentRow}>
          <div className={styles.textContent}>
            {isInline && sponsoredPill}
            {isInline && headingEl}
            <AdContent
              subheading={subheading}
              disclosureDetails={disclosureDetails}
              jsonContent={jsonContent}
              bodyStyle={theme.bodytextDefaultNone}
            />
          </div>

          {isInline && renderImage()}
        </div>
      </div>

      <ActionButtons
        acceptCopy={acceptCopy}
        declineCopy={declineCopy}
        onAccept={onAccept}
        onDecline={onDecline}
      />

      <Counter current={currentIndex} total={totalCount} />
    </div>
  );
}
